/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import PropTypes from "prop-types";
import "./IdentityHeader.css";

/**
 * The band at the top of a workspace's own dashboard: who this account is,
 * and the two figures it scans for.
 *
 * Shared by the donor and nonprofit dashboards because the design is one
 * component with the logo swapped for initials, not two that happen to look
 * alike -- a donor has no logo and no tagline, so those simply go unset and
 * the same markup renders the shorter card.
 *
 * Everything here is passed in already formatted. The component picks no
 * figures and reads no state: a dashboard's header has to agree with the
 * tiles directly beneath it, which only holds while both come out of the
 * same view model.
 */
class IdentityHeader extends Component {
  // The logo URL whose image has finished loading; see logoBusy.
  state = { logoLoaded: "" };

  /**
   * Whether the tile should show it is working: while the upload and the
   * record write run, and then until the new image has actually loaded --
   * the wait after the save, when the old picture was still showing and
   * nothing said the new one was on its way (CP-370). A failed load ends the
   * wait too; the spinner is not an error message.
   */
  logoBusy = (p) =>
    !!p.logoUploading || (!!p.logo && this.state.logoLoaded !== p.logo);

  logoSettled = (url) => () => {
    if (this.state.logoLoaded !== url) {
      this.setState({ logoLoaded: url });
    }
  };

  render() {
    const p = this.props.profile;
    if (!p) {
      return null;
    }
    const chips = p.chips || [];
    const stats = p.stats || [];
    return (
      <div className="Marketplace-card Marketplace-Identity">
        {/* The tile is the way in to changing the logo -- there is no separate
            button, because the image is the thing being changed and tapping it
            is what people try first. It used to open CheckPlus, which owns the
            organisation record; it is the picker itself now, writing the same
            record Our profile and Impact & proof do, and CheckPlus has a
            button of its own below (CP-370). A donor has no organisation
            record, so their tile is a plain div. */}
        {p.onPickLogo ? (
          /* The label is the wrapper rather than the tile, so the pen can sit
             over the tile's corner without being clipped by the tile's own
             overflow, and a click on either opens the picker. */
          <label
            className={
              "Marketplace-Identity-monoWrap" +
              (p.logoUploading ? " Marketplace-Identity-monoWrap--busy" : "")
            }
            title={p.logoLabel}
          >
            <span
              className="Marketplace-Identity-mono Marketplace-Identity-mono--link"
              style={{ background: p.monoBg, color: p.monoFg }}
            >
              {p.logo ? (
                <img
                  className="Marketplace-Identity-logo"
                  src={p.logo}
                  alt=""
                  onLoad={this.logoSettled(p.logo)}
                  onError={this.logoSettled(p.logo)}
                />
              ) : (
                p.mono
              )}
              {this.logoBusy(p) && (
                <span
                  className="Marketplace-Identity-busy"
                  role="status"
                  aria-label={p.logoBusyLabel}
                >
                  <span className="Marketplace-Identity-spinner" />
                </span>
              )}
            </span>
            {/* Says the tile can be changed, which an image on its own does
                not. */}
            <span className="Marketplace-Identity-pen" aria-hidden="true">
              ✎
            </span>
            <input
              type="file"
              className="Marketplace-Identity-fileInput"
              accept="image/jpeg,image/png"
              onChange={p.onPickLogo}
              disabled={p.logoUploading}
              aria-label={p.logoLabel}
            />
          </label>
        ) : (
          <div
            className="Marketplace-Identity-mono"
            style={{ background: p.monoBg, color: p.monoFg }}
          >
            {p.logo ? (
              <img className="Marketplace-Identity-logo" src={p.logo} alt="" />
            ) : (
              p.mono
            )}
          </div>
        )}
        <div className="Marketplace-Identity-info">
          <div className="Marketplace-Identity-name">{p.name}</div>
          {!!p.tagline && (
            <div className="Marketplace-Identity-tagline">{p.tagline}</div>
          )}
          {!!chips.length && (
            <div className="Marketplace-Identity-chips">
              {chips.map((chip) => {
                /* A donor's chips are plain strings and a nonprofit's carry a
                   tone, so both shapes are accepted here rather than making
                   the donor view model restate what it already says. */
                const c = typeof chip === "string" ? { label: chip } : chip;
                return (
                  <span
                    className={
                      "Marketplace-Identity-chip" +
                      (c.tone ? " Marketplace-Identity-chip--" + c.tone : "")
                    }
                    key={c.label}
                  >
                    {c.label}
                  </span>
                );
              })}
            </div>
          )}
          {(p.onPickColor || p.checkplusHref) && (
            <div className="Marketplace-Identity-actions">
              {p.onPickColor && (
                <label className="Marketplace-Identity-action">
                  <span
                    className="Marketplace-Identity-swatch"
                    style={{ background: p.colorValue }}
                  />
                  {p.colorLabel}
                  <input
                    type="color"
                    className="Marketplace-Identity-fileInput"
                    value={p.colorValue}
                    onChange={p.onPickColor}
                    aria-label={p.colorLabel}
                  />
                </label>
              )}
              {p.checkplusHref && (
                <a
                  className="Marketplace-Identity-action"
                  href={p.checkplusHref}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {p.checkplusLabel} ↗
                </a>
              )}
            </div>
          )}
          {/* Said where the controls are, before anything is changed. */}
          {!!p.logoShared && (
            <div className="Marketplace-Identity-note">{p.logoShared}</div>
          )}
          {[
            [p.logoState, p.logoBad, "logo"],
            [p.colorState, p.colorBad, "color"],
          ]
            .filter(([text]) => !!text)
            .map(([text, bad, key]) => (
              <div
                key={key}
                className={
                  "Marketplace-Identity-state" +
                  (bad ? " Marketplace-Identity-state--bad" : "")
                }
              >
                {text}
              </div>
            ))}
        </div>
        {!!stats.length && (
          <div className="Marketplace-Identity-stats">
            {stats.map((s) => (
              <div className="Marketplace-Identity-stat" key={s.id}>
                <div className="Marketplace-Identity-statValue">{s.value}</div>
                <div className="Marketplace-Identity-statLabel">{s.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
}

IdentityHeader.propTypes = {
  /* null before the account behind the dashboard is known -- the sample data
     has none, and a header naming nobody is worse than no header. */
  profile: PropTypes.object,
};

export default IdentityHeader;
